import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import JsonKeyValueEditor from '../mcp/JsonKeyValueEditor';
import {
  AgentA2AAuthMode,
  AgentRoutingMatchType,
  AgentRoutingTargetType,
} from '../../../../types/aiTypes';

type Props = {
  open: boolean;
  initialValue?: any | null;
  submitting?: boolean;
  agents?: any[];
  endpoints?: any[];
  entityTypes?: any[];
  onClose: () => void;
  onSubmit: (payload: any) => void;
};

const AUTH_MODES: AgentA2AAuthMode[] = [
  'none',
  'internal_mtls',
  'oauth2_client_credentials',
  'api_key',
];

const TARGET_TYPES: AgentRoutingTargetType[] = ['local_agent', 'a2a_endpoint'];

const MATCH_TYPES: AgentRoutingMatchType[] = [
  'route_path',
  'location_key',
  'intent_label',
  'explicit_agent_slug',
  'default_fallback',
];

const emptyA2AConfig = () => ({
  auth_mode: 'none' as AgentA2AAuthMode,
  inbound_enabled: true,
  outbound_enabled: false,
  allow_external_outbound: false,
  allow_external_inbound: false,
  advertised_card_overrides: {} as Record<string, any>,
});

const emptyForm = () => ({
  slug: '',
  name: '',
  description: '',
  can_orchestrate: false,
  default_model: '',
  default_temperature: '',
  is_active: true,
  notes: '',
  updated_by: '',
  prompt_versions: [] as any[],
  capability_scopes: [] as any[],
  context_providers: [] as any[],
  outgoing_rules: [] as any[],
  endpoint_scopes: [] as any[],
  a2a_config: emptyA2AConfig(),
});

const AgentEditorDialog: React.FC<Props> = ({
                                              open,
                                              initialValue,
                                              submitting,
                                              agents = [],
                                              endpoints = [],
                                              entityTypes = [],
                                              onClose,
                                              onSubmit,
                                            }) => {
  const [tab, setTab] = React.useState(0);
  const [form, setForm] = React.useState<any>(emptyForm());

  const isEdit = Boolean(form.id);

  React.useEffect(() => {
    if (!open) return;

    const seed = initialValue || {};

    setForm({
      ...emptyForm(),
      ...seed,
      default_temperature:
        seed.default_temperature === null ||
        seed.default_temperature === undefined
          ? ''
          : String(seed.default_temperature),
      prompt_versions: Array.isArray(seed.prompt_versions)
        ? seed.prompt_versions.map((p: any) => ({ ...p }))
        : [],
      capability_scopes: Array.isArray(seed.capability_scopes)
        ? seed.capability_scopes.map((s: any) => ({ ...s }))
        : [],
      context_providers: Array.isArray(seed.context_providers)
        ? seed.context_providers.map((c: any) => ({ ...c }))
        : [],
      outgoing_rules: Array.isArray(seed.outgoing_rules)
        ? seed.outgoing_rules.map((r: any) => ({ ...r }))
        : [],
      endpoint_scopes: Array.isArray(seed.endpoint_scopes)
        ? seed.endpoint_scopes.map((e: any) => ({ ...e }))
        : [],
      a2a_config: seed.a2a_config
        ? { ...emptyA2AConfig(), ...seed.a2a_config }
        : emptyA2AConfig(),
    });

    setTab(0);
  }, [initialValue, open]);

  const setField = (field: string, value: any) =>
    setForm((prev: any) => ({ ...prev, [field]: value }));

  const setConfigField = (field: string, value: any) =>
    setForm((prev: any) => ({
      ...prev,
      a2a_config: { ...prev.a2a_config, [field]: value },
    }));

  // --- Nested array helpers ---
  const addRow = (key: string, row: any) =>
    setForm((prev: any) => ({ ...prev, [key]: [...prev[key], row] }));

  const updateRow = (key: string, idx: number, patch: any) =>
    setForm((prev: any) => {
      const next = [...prev[key]];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, [key]: next };
    });

  const removeRow = (key: string, idx: number) =>
    setForm((prev: any) => ({
      ...prev,
      [key]: prev[key].filter((_: any, i: number) => i !== idx),
    }));

  const handleSubmit = () => {
    if (!String(form.slug || '').trim()) {
      window.alert('Slug is required.');
      setTab(0);
      return;
    }
    if (!String(form.name || '').trim()) {
      window.alert('Name is required.');
      setTab(0);
      return;
    }

    for (const p of form.prompt_versions) {
      if (
        p.version_number === '' ||
        p.version_number === null ||
        p.version_number === undefined
      ) {
        window.alert('Every prompt version needs a version number.');
        setTab(1);
        return;
      }
      if (!String(p.prompt_text || '').trim()) {
        window.alert('Every prompt version needs prompt text.');
        setTab(1);
        return;
      }
    }

    for (const s of form.capability_scopes) {
      if (!String(s.capability_tag || '').trim()) {
        window.alert('Every capability scope needs a capability tag.');
        setTab(2);
        return;
      }
    }

    for (const c of form.context_providers) {
      if (!c.entity_type) {
        window.alert('Every context provider needs an entity type.');
        setTab(3);
        return;
      }
    }

    for (const r of form.outgoing_rules) {
      if (r.target_type === 'a2a_endpoint' && !r.target_endpoint) {
        window.alert('An a2a_endpoint routing rule needs a target endpoint.');
        setTab(4);
        return;
      }
      if (r.target_type === 'local_agent' && !r.target_agent) {
        window.alert('A local_agent routing rule needs a target agent.');
        setTab(4);
        return;
      }
    }

    for (const e of form.endpoint_scopes) {
      if (!e.endpoint) {
        window.alert('Every endpoint scope needs an endpoint.');
        setTab(5);
        return;
      }
    }

    const payload = {
      ...form,
      slug: String(form.slug).trim(),
      name: String(form.name).trim(),
      default_temperature:
        String(form.default_temperature).trim() === ''
          ? null
          : Number(form.default_temperature),
      prompt_versions: form.prompt_versions.map((p: any) => ({
        ...p,
        version_number: Number(p.version_number),
      })),
    };

    onSubmit(payload);
  };

  // Exclude the agent being edited from local-agent routing targets.
  const targetAgentOptions = agents.filter(
    (a) => String(a.id) !== String(form.id)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Agent' : 'Add Agent'}</DialogTitle>

      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          <Tab label="Definition" />
          <Tab label={`Prompts (${form.prompt_versions.length})`} />
          <Tab label={`Capabilities (${form.capability_scopes.length})`} />
          <Tab label={`Context (${form.context_providers.length})`} />
          <Tab label={`Routing (${form.outgoing_rules.length})`} />
          <Tab label="A2A" />
        </Tabs>

        {/* --- Definition --- */}
        {tab === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                label="Slug"
                value={form.slug}
                onChange={(e) => setField('slug', e.target.value)}
                inputProps={{ maxLength: 64 }}
                helperText="Stable identifier, max 64 chars"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                size="small"
                label="Name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                inputProps={{ maxLength: 128 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Description"
                value={form.description || ''}
                onChange={(e) => setField('description', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Default Model"
                value={form.default_model || ''}
                onChange={(e) => setField('default_model', e.target.value)}
                inputProps={{ maxLength: 64 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Default Temperature"
                value={form.default_temperature}
                onChange={(e) => setField('default_temperature', e.target.value)}
                inputProps={{ step: 0.1, min: 0, max: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.can_orchestrate}
                    onChange={(e) => setField('can_orchestrate', e.target.checked)}
                  />
                }
                label="Can orchestrate (parent agent)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.is_active}
                    onChange={(e) => setField('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Notes"
                value={form.notes || ''}
                onChange={(e) => setField('notes', e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Updated By"
                value={form.updated_by || ''}
                onChange={(e) => setField('updated_by', e.target.value)}
              />
            </Grid>
          </Grid>
        ) : null}

        {/* --- Prompt Versions --- */}
        {tab === 1 ? (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  addRow('prompt_versions', {
                    version_number:
                      (form.prompt_versions.reduce(
                        (max: number, p: any) =>
                          Math.max(max, Number(p.version_number) || 0),
                        0
                      ) || 0) + 1,
                    prompt_text: '',
                    is_active: form.prompt_versions.length === 0,
                    change_summary: '',
                  })
                }
              >
                Add Prompt Version
              </Button>
            </Box>

            {form.prompt_versions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No prompt versions. The active version is used as the
                system prompt for this agent.
              </Typography>
            ) : null}

            {form.prompt_versions.map((p: any, idx: number) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Version #"
                      value={p.version_number ?? ''}
                      onChange={(e) =>
                        updateRow('prompt_versions', idx, {
                          version_number: e.target.value,
                        })
                      }
                    />
                  </Grid>

                  <Grid item xs={6} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Change Summary"
                      value={p.change_summary || ''}
                      onChange={(e) =>
                        updateRow('prompt_versions', idx, {
                          change_summary: e.target.value,
                        })
                      }
                      inputProps={{ maxLength: 255 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!p.is_active}
                          onChange={(e) => {
                            // Only one active version at a time.
                            setForm((prev: any) => ({
                              ...prev,
                              prompt_versions: prev.prompt_versions.map(
                                (row: any, i: number) => ({
                                  ...row,
                                  is_active:
                                    i === idx ? e.target.checked : false,
                                })
                              ),
                            }));
                          }}
                        />
                      }
                      label="Active"
                    />

                    <IconButton
                      color="error"
                      onClick={() => removeRow('prompt_versions', idx)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={5}
                      size="small"
                      label="Prompt Text"
                      value={p.prompt_text || ''}
                      onChange={(e) =>
                        updateRow('prompt_versions', idx, {
                          prompt_text: e.target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        ) : null}

        {/* --- Capability Scopes --- */}
        {tab === 2 ? (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="caption" color="text.secondary">
                An empty scope list imposes no ceiling (agent inherits all
                capabilities). Adding an allow list restricts to those tags.
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  addRow('capability_scopes', {
                    capability_tag: '',
                    allow: true,
                  })
                }
              >
                Add Scope
              </Button>
            </Box>

            {form.capability_scopes.map((s: any, idx: number) => (
              <Grid container spacing={2} key={idx} alignItems="center">
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Capability Tag / Category"
                    value={s.capability_tag || ''}
                    onChange={(e) =>
                      updateRow('capability_scopes', idx, {
                        capability_tag: e.target.value,
                      })
                    }
                    inputProps={{ maxLength: 128 }}
                  />
                </Grid>

                <Grid item xs={8} md={3}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Effect"
                    value={s.allow === false ? 'deny' : 'allow'}
                    onChange={(e) =>
                      updateRow('capability_scopes', idx, {
                        allow: e.target.value === 'allow',
                      })
                    }
                  >
                    <MenuItem value="allow">allow</MenuItem>
                    <MenuItem value="deny">deny</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={4} md={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeRow('capability_scopes', idx)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Stack>
        ) : null}

        {/* --- Context Providers --- */}
        {tab === 3 ? (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  addRow('context_providers', {
                    entity_type: '',
                    priority: 0,
                    is_required: true,
                  })
                }
              >
                Add Context Provider
              </Button>
            </Box>

            {form.context_providers.map((c: any, idx: number) => (
              <Grid container spacing={2} key={idx} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Entity Type"
                    value={c.entity_type || ''}
                    onChange={(e) =>
                      updateRow('context_providers', idx, {
                        entity_type: e.target.value,
                      })
                    }
                  >
                    {entityTypes.map((et) => (
                      <MenuItem key={String(et.id)} value={et.id}>
                        {et.label || et.key || et.name || et.id}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={6} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Priority"
                    value={c.priority ?? 0}
                    onChange={(e) =>
                      updateRow('context_providers', idx, {
                        priority: Number(e.target.value),
                      })
                    }
                  />
                </Grid>

                <Grid item xs={4} md={2} display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!c.is_required}
                        onChange={(e) =>
                          updateRow('context_providers', idx, {
                            is_required: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Required"
                  />
                </Grid>

                <Grid item xs={2} md={1}>
                  <IconButton
                    color="error"
                    onClick={() => removeRow('context_providers', idx)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Stack>
        ) : null}

        {/* --- Routing Rules --- */}
        {tab === 4 ? (
          <Stack spacing={2}>
            {!form.can_orchestrate ? (
              <Typography variant="body2" color="warning.main">
                This agent is not marked as an orchestrator. Routing rules only
                take effect for orchestrator agents.
              </Typography>
            ) : null}

            <Box display="flex" justifyContent="flex-end">
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  addRow('outgoing_rules', {
                    target_type: 'local_agent',
                    match_type: 'default_fallback',
                    match_value: '',
                    required_permission: '',
                    priority:
                      (form.outgoing_rules.reduce(
                        (max: number, r: any) =>
                          Math.max(max, Number(r.priority) || 0),
                        0
                      ) || 0) + 10,
                    is_active: true,
                    target_agent: null,
                    target_endpoint: null,
                  })
                }
              >
                Add Routing Rule
              </Button>
            </Box>

            {form.outgoing_rules.map((r: any, idx: number) => (
              <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Target Type"
                      value={r.target_type || 'local_agent'}
                      onChange={(e) =>
                        updateRow('outgoing_rules', idx, {
                          target_type: e.target.value,
                          target_agent: null,
                          target_endpoint: null,
                        })
                      }
                    >
                      {TARGET_TYPES.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={6} md={3}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label="Match Type"
                      value={r.match_type || 'default_fallback'}
                      onChange={(e) =>
                        updateRow('outgoing_rules', idx, {
                          match_type: e.target.value,
                        })
                      }
                    >
                      {MATCH_TYPES.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Match Value"
                      value={r.match_value || ''}
                      onChange={(e) =>
                        updateRow('outgoing_rules', idx, {
                          match_value: e.target.value,
                        })
                      }
                      inputProps={{ maxLength: 128 }}
                      disabled={r.match_type === 'default_fallback'}
                    />
                  </Grid>

                  <Grid item xs={6} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Priority"
                      value={r.priority ?? 0}
                      onChange={(e) =>
                        updateRow('outgoing_rules', idx, {
                          priority: Number(e.target.value),
                        })
                      }
                    />
                  </Grid>

                  {r.target_type === 'a2a_endpoint' ? (
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        label="Target Endpoint"
                        value={r.target_endpoint || ''}
                        onChange={(e) =>
                          updateRow('outgoing_rules', idx, {
                            target_endpoint: e.target.value,
                          })
                        }
                      >
                        {endpoints.map((ep) => (
                          <MenuItem key={String(ep.id)} value={ep.id}>
                            {ep.name} ({ep.slug})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  ) : (
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        label="Target Agent"
                        value={r.target_agent || ''}
                        onChange={(e) =>
                          updateRow('outgoing_rules', idx, {
                            target_agent: e.target.value,
                          })
                        }
                      >
                        {targetAgentOptions.map((a) => (
                          <MenuItem key={String(a.id)} value={a.id}>
                            {a.name} ({a.slug})
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  <Grid item xs={12} md={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Required Permission"
                      value={r.required_permission || ''}
                      onChange={(e) =>
                        updateRow('outgoing_rules', idx, {
                          required_permission: e.target.value,
                        })
                      }
                      inputProps={{ maxLength: 128 }}
                      helperText="RBAC permission the caller must hold (optional)"
                    />
                  </Grid>

                  <Grid item xs={12} md={2} display="flex" alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!r.is_active}
                          onChange={(e) =>
                            updateRow('outgoing_rules', idx, {
                              is_active: e.target.checked,
                            })
                          }
                        />
                      }
                      label="Active"
                    />

                    <IconButton
                      color="error"
                      onClick={() => removeRow('outgoing_rules', idx)}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Stack>
        ) : null}

        {/* --- A2A --- */}
        {tab === 5 ? (
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                A2A Configuration
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Auth Mode"
                    value={form.a2a_config.auth_mode || 'none'}
                    onChange={(e) => setConfigField('auth_mode', e.target.value)}
                  >
                    {AUTH_MODES.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Stack direction="row" flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.a2a_config.inbound_enabled}
                          onChange={(e) =>
                            setConfigField('inbound_enabled', e.target.checked)
                          }
                        />
                      }
                      label="Inbound"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!form.a2a_config.outbound_enabled}
                          onChange={(e) =>
                            setConfigField('outbound_enabled', e.target.checked)
                          }
                        />
                      }
                      label="Outbound"
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form.a2a_config.allow_external_outbound}
                        onChange={(e) =>
                          setConfigField(
                            'allow_external_outbound',
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Allow external outbound"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!form.a2a_config.allow_external_inbound}
                        onChange={(e) =>
                          setConfigField(
                            'allow_external_inbound',
                            e.target.checked
                          )
                        }
                      />
                    }
                    label="Allow external inbound"
                  />
                </Grid>

                <Grid item xs={12}>
                  <JsonKeyValueEditor
                    label="Advertised Card Overrides"
                    value={form.a2a_config.advertised_card_overrides || {}}
                    onChange={(next) =>
                      setConfigField('advertised_card_overrides', next)
                    }
                    helperText="Overrides merged into the published Agent Card"
                  />
                </Grid>
              </Grid>
            </Paper>

            <Divider />

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle2" fontWeight={700}>
                Endpoint Scopes
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  addRow('endpoint_scopes', { endpoint: '', allow: true })
                }
              >
                Add Endpoint Scope
              </Button>
            </Box>

            {form.endpoint_scopes.map((s: any, idx: number) => (
              <Grid container spacing={2} key={idx} alignItems="center">
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Endpoint"
                    value={s.endpoint || ''}
                    onChange={(e) =>
                      updateRow('endpoint_scopes', idx, {
                        endpoint: e.target.value,
                      })
                    }
                  >
                    {endpoints.map((ep) => (
                      <MenuItem key={String(ep.id)} value={ep.id}>
                        {ep.name} ({ep.slug})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={8} md={3}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    label="Effect"
                    value={s.allow === false ? 'deny' : 'allow'}
                    onChange={(e) =>
                      updateRow('endpoint_scopes', idx, {
                        allow: e.target.value === 'allow',
                      })
                    }
                  >
                    <MenuItem value="allow">allow</MenuItem>
                    <MenuItem value="deny">deny</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={4} md={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeRow('endpoint_scopes', idx)}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!!submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!!submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Agent'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentEditorDialog;
